import { useHoldRepeat } from '@src/hooks/useHoldRepeat';
import React from 'react';
import styled from 'styled-components';

/**
 * Bordered number field with − / + steppers around a typable value
 * (PLAT-11432). The two halves of this pattern already existed apart: the
 * canvas floating toolbar had hold-to-repeat steppers with a read-only value,
 * `NumericalInput` had a typable field with no steppers. This is both, and is
 * the design system's "Number input / stepper" bordered primitive.
 *
 * Pure UI — the commit contract matches `NumericalInput` (emits raw numeric
 * text without the unit, or '' when cleared) so it is a drop-in replacement.
 */
interface StepperNumberInputProps {
  defaultValue?: number | string; // external canonical numeric string/number (no unit)
  onChange: (value: number | string) => void; // emitted raw numeric text (no unit) or '' when cleared
  unit?: string; // unit suffix displayed only (not included in emitted value)
  min?: number;
  max?: number;
  step?: number;
  // Where the steppers start when the field is empty. Without it an empty
  // field steps from `min`, which is a floor, not a sensible starting size.
  stepFromWhenEmpty?: number;
  disabled?: boolean;
  className?: string;
  decrementLabel?: string;
  incrementLabel?: string;
}

export const StepperNumberInput: React.FC<StepperNumberInputProps> = props => {
  const { min, max, step = 1, stepFromWhenEmpty } = props;
  // Raw internal value WITHOUT unit; empty string represents cleared state.
  const [rawValue, setRawValue] = React.useState<string>(() =>
    props.defaultValue === undefined ? '' : String(props.defaultValue),
  );
  // The steppers read the value through this ref, not through the render's
  // `rawValue`: `useHoldRepeat` re-invokes the closure it captured at
  // hold-start, so closing over the state would freeze a hold at one step.
  const rawValueRef = React.useRef<string>(rawValue);
  const setRaw = React.useCallback((next: string) => {
    rawValueRef.current = next;
    setRawValue(next);
  }, []);
  const [editing, setEditing] = React.useState(false);
  const [error, setError] = React.useState<string>('');
  // Last external value we observed. Syncing off a *change* in it — rather
  // than off a mismatch with our own state — is what keeps a stale echo (the
  // parent still on the old value after we emit) from flashing back, without
  // a guard flag that can latch and deafen the field to later updates.
  const lastExternalRef = React.useRef<string>(rawValue);
  const holdDec = useHoldRepeat();
  const holdInc = useHoldRepeat();

  // Sync when defaultValue changes externally. An external value is recorded
  // even while editing, so a change that arrives mid-edit doesn't land on blur
  // and clobber what the user just typed.
  React.useEffect(() => {
    const next = props.defaultValue;
    const normalized = next === undefined ? '' : String(next);
    const changed = normalized !== lastExternalRef.current;
    lastExternalRef.current = normalized;
    if (!changed || editing) return;
    setRaw(normalized);
  }, [props.defaultValue, editing, setRaw]);

  const excludeUnit = (value: string): string => {
    if (!props.unit) return value;
    const trimmed = value.trim();
    if (trimmed.endsWith(props.unit) && trimmed !== props.unit) {
      return trimmed.substring(0, trimmed.lastIndexOf(props.unit)).trimEnd();
    }
    return value;
  };

  const validate = (v: string): string => {
    if (v === '') return ''; // cleared is always valid
    if (isNaN(Number(v))) return 'Please enter a number';
    if (min !== undefined && Number(v) < min) return `Value must be ≥ ${min}`;
    if (max !== undefined && Number(v) > max) return `Value must be ≤ ${max}`;
    return '';
  };

  const emit = (next: string) => {
    setRaw(next);
    props.onChange(next === '' ? '' : next);
  };

  const commit = (input: string) => {
    const next = excludeUnit(input).trim();
    const validation = validate(next);
    setError(validation);
    if (validation) return; // keep editing until fixed
    emit(next);
    setEditing(false);
  };

  // Steppers work off the committed numeric value, so a click mid-edit steps
  // from what the user typed rather than the stale external value.
  const currentNumber = (): number => {
    const raw = rawValueRef.current;
    const parsed = Number(excludeUnit(raw).trim());
    if (raw.trim() !== '' && !isNaN(parsed)) return Math.round(parsed);
    return stepFromWhenEmpty ?? min ?? 0;
  };
  const clamp = (value: number): number => {
    let next = value;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    return next;
  };
  const atMin = min !== undefined && currentNumber() <= min;
  const atMax = max !== undefined && currentNumber() >= max;
  const canDecrement = !props.disabled && !atMin;
  const canIncrement = !props.disabled && !atMax;

  // Stepping never ends an edit session: the buttons `preventDefault()` their
  // mousedown to keep focus in the input, so clearing `editing` here would
  // append the display-only unit to the value the user is still typing in.
  const stepBy = (direction: 1 | -1) => {
    const next = clamp(currentNumber() + direction * step);
    if (String(next) === rawValueRef.current) return;
    setError('');
    emit(String(next));
  };
  const stepDown = () => stepBy(-1);
  const stepUp = () => stepBy(1);

  const displayValue = editing
    ? rawValue
    : rawValue !== '' && !isNaN(Number(rawValue)) && props.unit
      ? `${rawValue}${props.unit}`
      : rawValue;

  return (
    <Container className={props.className}>
      <Field $hasError={!!error} $disabled={!!props.disabled}>
        {/* Click for one step, hold to repeat. Bounded to [min, max]; buttons
         * go disabled at a bound so the user has a clear visual stop. Wired on
         * mouse + touch + keyboard so all input methods feel the same. */}
        <StepButton
          type="button"
          tabIndex={-1}
          disabled={!canDecrement}
          aria-label={props.decrementLabel ?? 'Decrease value'}
          onMouseDown={e => {
            // Keep focus in the input so the field's focus ring doesn't flicker.
            e.preventDefault();
            if (canDecrement) holdDec.start(stepDown);
          }}
          onMouseUp={holdDec.stop}
          onMouseLeave={holdDec.stop}
          onTouchStart={e => {
            if (!canDecrement) return;
            // Prevent the synthesized mousedown that would otherwise
            // double-fire the action immediately after touchstart.
            e.preventDefault();
            holdDec.start(stepDown);
          }}
          onTouchEnd={holdDec.stop}
          onTouchCancel={holdDec.stop}
        >
          −
        </StepButton>
        <TextInput
          value={displayValue}
          type="text"
          inputMode="decimal"
          role="textbox"
          disabled={props.disabled}
          aria-invalid={!!error}
          onFocus={e => {
            if (props.disabled) return;
            setEditing(true);
            // select only numeric part (rawValue already stripped) after render frame
            requestAnimationFrame(() => e.target.select());
          }}
          onChange={e => {
            if (props.disabled) return;
            const incoming = excludeUnit(e.target.value);
            setRaw(incoming);
            if (!editing) setEditing(true);
            // live validation feedback (optional) – only set error, don't block typing
            setError(validate(incoming.trim()));
          }}
          onKeyDown={e => {
            if (props.disabled) return;
            if (e.key === 'ArrowUp' && canIncrement) {
              e.preventDefault();
              stepUp();
            } else if (e.key === 'ArrowDown' && canDecrement) {
              e.preventDefault();
              stepDown();
            } else if (e.key === 'Enter') {
              e.preventDefault();
              commit(e.currentTarget.value);
            }
          }}
          onBlur={e => {
            if (props.disabled) return;
            commit(e.target.value);
          }}
        />
        <StepButton
          type="button"
          tabIndex={-1}
          disabled={!canIncrement}
          aria-label={props.incrementLabel ?? 'Increase value'}
          onMouseDown={e => {
            e.preventDefault();
            if (canIncrement) holdInc.start(stepUp);
          }}
          onMouseUp={holdInc.stop}
          onMouseLeave={holdInc.stop}
          onTouchStart={e => {
            if (!canIncrement) return;
            e.preventDefault();
            holdInc.start(stepUp);
          }}
          onTouchEnd={holdInc.stop}
          onTouchCancel={holdInc.stop}
        >
          +
        </StepButton>
      </Field>
      {error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/*
 * Bordered-field-primitive hover/focus standard — see
 * docs/design-system.md "Form Fields (Bordered Primitives)". The border lives
 * on this wrapper and the focusable element is the inner input, so the state
 * escalates on `:focus-within`. Single channel: border-color only, no outline
 * halo. Error variant keeps the destructive border.
 */
const Field = styled.div<{ $hasError: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid
    ${p => (p.$hasError ? 'var(--destructive)' : 'var(--border)')};
  border-radius: var(--radius);
  background-color: transparent;
  transition: border-color var(--transition-fast);
  opacity: ${p => (p.$disabled ? 0.5 : 1)};

  &:hover {
    border-color: ${p =>
      p.$hasError ? 'var(--destructive)' : 'var(--text-muted)'};
  }
  &:focus-within {
    border-color: ${p =>
      p.$hasError ? 'var(--destructive)' : 'var(--accent)'};
  }
`;

const StepButton = styled.button`
  flex: 0 0 auto;
  width: 34px;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-md);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  user-select: none;
  -webkit-touch-callout: none;
  /* Prevent the browser from interpreting a long-press on touch as a text
   * selection / context-menu gesture while the user is holding to repeat. */
  touch-action: manipulation;

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  &:active:not(:disabled) {
    background: var(--border);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    color: var(--text-muted);
  }
`;

const TextInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  padding: 11px 4px;
  text-align: center;
  color: var(--foreground);
  background-color: transparent;
  border: none;
  outline: none;
  font-family: var(--font-primary);
  font-size: var(--text-sm);

  &:disabled {
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  margin-top: 2px;
  color: var(--destructive);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: 1.2;
`;
